// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The room the machine is standing in (M3-08, M3-09).
//
// A pinball machine is mostly chrome and glass, and chrome and glass are almost entirely
// *reflection*. A ball lit by lights and reflecting nothing is a grey sphere with a highlight
// on it; the same ball with something to mirror reads as steel instantly, and it is the single
// cheapest thing that separates a rendered pinball machine from a real one. The playfield glass
// has the same problem more acutely — a sheet of glass with nothing to reflect is invisible,
// so without this there is no point drawing it at all.
//
// **Generated, like everything else.** Zero image files is not a stylistic preference; it
// removes all asset licensing, removes the Store's third-party licence manifest, and gives the
// Machine Builder a vocabulary with no upload-moderation surface. So the environment is a
// handful of coloured emissive planes in a small scene, prefiltered into a cube map once at
// load and then thrown away. Nothing is fetched, nothing is decoded, and it costs one
// render-to-texture during startup.
//
// The *content* is a description — a list of glowing panels — so it can be read and reasoned
// about without a GPU. Only the prefiltering needs a renderer, and that is four lines.

import {
  BackSide,
  BoxGeometry,
  type BufferGeometry,
  Color,
  Mesh,
  MeshBasicMaterial,
  PMREMGenerator,
  PlaneGeometry,
  Scene,
  type Texture,
  type WebGLRenderer,
} from 'three'

/** One glowing surface in the room, in metres about the machine's own origin. */
export interface EnvPanel {
  readonly colour: number
  readonly intensity: number
  /** Width and height of the panel. */
  readonly size: readonly [number, number]
  readonly position: readonly [number, number, number]
  /** Euler XYZ, radians. */
  readonly rotation: readonly [number, number, number]
}

/**
 * An arcade, reduced to the six things a reflection actually shows.
 *
 * Not a room: a room modelled honestly would be a hundred surfaces of which a 27 mm chrome
 * sphere shows about four. What a ball on a pinball playfield actually reflects is the lit
 * backglass above it, the dark ceiling, the pale floor bouncing light back up, and whatever
 * neon the room has along the walls. Those are the panels, and nothing else earns its place.
 *
 * The asymmetry is deliberate and load-bearing. A symmetrical environment gives a rolling ball
 * a highlight that barely moves, which reads as a *painted* highlight; the whole reason to have
 * a reflection at all is that it sweeps as the ball crosses the playfield.
 */
export const ARCADE_PANELS: readonly EnvPanel[] = [
  // The machine's own backglass, above and behind. The brightest thing in the room by far,
  // and the reflection a player unconsciously expects to see sliding across the ball.
  {
    colour: 0xf1efe8,
    intensity: 2.6,
    size: [1.1, 0.7],
    position: [0, 1.65, -0.95],
    rotation: [0.2, 0, 0],
  },
  // Ceiling: dark, but not black. A black ceiling makes the top of every chrome surface a
  // hole, and a real arcade ceiling is at least dimly lit by the machines under it.
  {
    colour: 0x1a2025,
    intensity: 0.5,
    size: [6, 6],
    position: [0, 3.0, 0],
    rotation: [Math.PI / 2, 0, 0],
  },
  // Floor: pale, bouncing the machines' own light back up. This is what stops the underside
  // of the ball being a void.
  {
    colour: 0x434a4d,
    intensity: 0.55,
    size: [6, 6],
    position: [0, -0.6, 0],
    rotation: [-Math.PI / 2, 0, 0],
  },
  // Cool and warm neutral reflections retain moving highlights without painting every table
  // with the same saturated purple room colour. Gameplay lamps supply the themed colour.
  {
    colour: 0xbdd0d5,
    intensity: 1.5,
    size: [0.35, 3.2],
    position: [-1.9, 1.5, 0],
    rotation: [0, Math.PI / 2, 0],
  },
  {
    colour: 0xe0d4be,
    intensity: 1.3,
    size: [0.35, 3.2],
    position: [1.9, 1.5, 0],
    rotation: [0, -Math.PI / 2, 0],
  },
  // A cool wash from in front, standing in for the room the player is in. Weak, because a
  // player's own side of the machine is the dark side.
  {
    colour: 0x88969b,
    intensity: 0.7,
    size: [4, 2.4],
    position: [0, 1.2, 2.6],
    rotation: [0, Math.PI, 0],
  },
]

/** A table can replace the six reflection colours without changing their physical layout. */
export function environmentPanelsFor(colours?: readonly string[]): readonly EnvPanel[] {
  if (colours === undefined) return ARCADE_PANELS
  if (colours.length !== ARCADE_PANELS.length || colours.some(c => !/^#[0-9a-f]{6}$/i.test(c)))
    throw new Error('A reflection palette requires six RGB colours')
  return ARCADE_PANELS.map((panel, i) => ({ ...panel, colour: Number.parseInt(colours[i]!.slice(1), 16) }))
}

export interface BuiltEnvironment {
  /** Assign to `scene.environment`. Everything physical in the scene picks it up. */
  readonly texture: Texture
  readonly dispose: () => void
}

/**
 * Prefilter the panels into an environment map.
 *
 * `PMREMGenerator` is what turns a scene into something a rough surface can sample cheaply: it
 * produces the mip chain a roughness value indexes into, so a mirror-smooth ball and a matte
 * rail read the same environment at the right blur without either of them tracing anything.
 *
 * Everything it needs is disposed here. The source scene, its geometries and its materials all
 * exist for the length of one render and would otherwise sit on the GPU for the session — a
 * small leak that only shows up as memory on a headset, which is where it matters most.
 */
export function buildEnvironment(
  renderer: WebGLRenderer,
  panels: readonly EnvPanel[] = ARCADE_PANELS,
): BuiltEnvironment {
  const scene = new Scene()
  // A dark shell so the map has no holes. Without it, every direction that misses a panel is
  // pure black, and a chrome ball ends up with hard black wedges between its reflections.
  const shell = new BoxGeometry(14, 14, 14)
  const shellMaterial = new MeshBasicMaterial({ color: 0x070a14, side: BackSide })
  scene.add(new Mesh(shell, shellMaterial))

  const geometries: BufferGeometry[] = [shell]
  const materials: MeshBasicMaterial[] = [shellMaterial]

  for (const panel of panels) {
    const geometry = new PlaneGeometry(panel.size[0], panel.size[1])
    const colour = new Color(panel.colour).multiplyScalar(panel.intensity)
    const material = new MeshBasicMaterial({ color: colour })
    const mesh = new Mesh(geometry, material)
    mesh.position.set(panel.position[0], panel.position[1], panel.position[2])
    mesh.rotation.set(panel.rotation[0], panel.rotation[1], panel.rotation[2])
    scene.add(mesh)
    geometries.push(geometry)
    materials.push(material)
  }

  const pmrem = new PMREMGenerator(renderer)
  const target = pmrem.fromScene(scene)
  pmrem.dispose()

  for (const g of geometries) g.dispose()
  for (const m of materials) m.dispose()

  return {
    texture: target.texture,
    dispose: () => target.dispose(),
  }
}
